import { useState, useEffect } from 'react'
import { 
  Save, 
  Upload, 
  Key, 
  Shield, 
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Settings,
  Smartphone
} from 'lucide-react'

interface StoreCredentials {
  googlePlay: {
    serviceAccountEmail: string
    serviceAccountKey: string
    packageName: string
    connected: boolean
    lastSync: string | null
  }
  appleStore: {
    teamId: string
    keyId: string
    issuerId: string
    privateKey: string
    bundleId: string
    connected: boolean
    lastSync: string | null
  }
}

export function StoreSettings() {
  const [credentials, setCredentials] = useState<StoreCredentials>({
    googlePlay: {
      serviceAccountEmail: '',
      serviceAccountKey: '',
      packageName: 'com.servi.*',
      connected: false,
      lastSync: null
    },
    appleStore: {
      teamId: '',
      keyId: '',
      issuerId: '',
      privateKey: '',
      bundleId: 'com.servi.*',
      connected: false,
      lastSync: null
    }
  })
  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')

  // Load existing credentials on component mount
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        console.log('📱 Loading store credentials...')
        const response = await fetch('/api/stores/credentials', {
          method: 'GET',
          credentials: 'include'
        })

        if (response.ok) {
          const data = await response.json()
          console.log('✅ Credentials loaded:', data)
          setCredentials(data)
        } else {
          console.error('❌ Failed to load credentials:', response.status)
        }
      } catch (error) {
        console.error('❌ Error loading credentials:', error)
      }
    }

    loadCredentials()
  }, [])

  const updateGooglePlay = (updates: Partial<StoreCredentials['googlePlay']>) => {
    setCredentials(prev => ({
      ...prev,
      googlePlay: { ...prev.googlePlay, ...updates }
    }))
  }

  const updateAppleStore = (updates: Partial<StoreCredentials['appleStore']>) => {
    setCredentials(prev => ({
      ...prev,
      appleStore: { ...prev.appleStore, ...updates }
    }))
  }

  const handleFileUpload = (platform: 'googlePlay' | 'appleStore', file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      if (platform === 'googlePlay') {
        updateGooglePlay({ serviceAccountKey: content })
      } else {
        updateAppleStore({ privateKey: content })
      }
    }
    reader.readAsText(file)
  }

  const testConnection = async (platform: 'googlePlay' | 'appleStore') => {
    setLoading(true)
    try {
      console.log(`🔗 Testing ${platform} connection...`)
      const platformCredentials = platform === 'googlePlay' ? credentials.googlePlay : credentials.appleStore
      
      const response = await fetch('/api/stores/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          platform,
          credentials: platformCredentials
        })
      })

      const result = await response.json()
      
      if (response.ok && result.connected) {
        console.log(`✅ ${platform} connection successful`)
        if (platform === 'googlePlay') {
          updateGooglePlay({ 
            connected: true, 
            lastSync: result.lastSync 
          })
        } else {
          updateAppleStore({ 
            connected: true, 
            lastSync: result.lastSync 
          })
        }
      } else {
        console.error(`❌ ${platform} connection failed:`, result.error)
      }
    } catch (error) {
      console.error(`Failed to connect to ${platform}:`, error)
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    setSaveStatus('saving')
    try {
      console.log('💾 Saving store settings...')
      const response = await fetch('/api/stores/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          googlePlay: credentials.googlePlay,
          appleStore: credentials.appleStore
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save settings')
      }

      const result = await response.json()
      console.log('✅ Settings saved:', result)
      
      setSaveStatus('saved')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch (error) {
      console.error('❌ Error saving settings:', error)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Store Settings</h1>
          <p className="text-gray-600 mt-1">Configure connections to Google Play Store and Apple App Store</p>
        </div>
        <button 
          onClick={saveSettings}
          disabled={saveStatus === 'saving'}
          className="btn-primary flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>
            {saveStatus === 'saving' ? 'Saving...' : 
             saveStatus === 'saved' ? 'Saved!' : 'Save Settings'}
          </span>
        </button>
      </div>

      {/* Google Play Store Settings */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Smartphone className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Google Play Store</h3>
                <p className="text-sm text-gray-600">Configure API access for app publishing</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {credentials.googlePlay.connected ? (
                <div className="flex items-center space-x-1 text-success-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-gray-500">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Not Connected</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Account Email
              </label>
              <input
                type="email"
                value={credentials.googlePlay.serviceAccountEmail}
                onChange={(e) => updateGooglePlay({ serviceAccountEmail: e.target.value })}
                className="input"
                placeholder="service-account@project.iam.gserviceaccount.com"
              />
              <p className="mt-1 text-xs text-gray-500">
                The email address of your Google Cloud service account
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name Pattern
              </label>
              <input
                type="text"
                value={credentials.googlePlay.packageName}
                onChange={(e) => updateGooglePlay({ packageName: e.target.value })}
                className="input"
                placeholder="com.servi.*"
              />
              <p className="mt-1 text-xs text-gray-500">
                Base package name pattern for your apps
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Account Key (JSON)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input
                type="file"
                accept=".json"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileUpload('googlePlay', file)
                }}
                className="hidden"
                id="google-key-upload"
              />
              <label htmlFor="google-key-upload" className="cursor-pointer">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Click to upload service account JSON key
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Download from Google Cloud Console
                  </p>
                </div>
              </label>
              {credentials.googlePlay.serviceAccountKey && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">Service account key uploaded</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => testConnection('googlePlay')}
                disabled={loading || !credentials.googlePlay.serviceAccountEmail || !credentials.googlePlay.serviceAccountKey}
                className="btn-secondary"
              >
                {loading ? 'Testing...' : 'Test Connection'}
              </button>
              <a
                href="https://console.cloud.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-600 hover:text-brand-700 flex items-center space-x-1"
              >
                <span>Google Cloud Console</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {credentials.googlePlay.lastSync && (
              <span className="text-xs text-gray-500">
                Last synced: {new Date(credentials.googlePlay.lastSync).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Apple App Store Settings */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Smartphone className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Apple App Store</h3>
                <p className="text-sm text-gray-600">Configure App Store Connect API access</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {credentials.appleStore.connected ? (
                <div className="flex items-center space-x-1 text-success-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-gray-500">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Not Connected</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team ID
              </label>
              <input
                type="text"
                value={credentials.appleStore.teamId}
                onChange={(e) => updateAppleStore({ teamId: e.target.value })}
                className="input"
                placeholder="ABC123DEF4"
              />
              <p className="mt-1 text-xs text-gray-500">
                Your Apple Developer Team ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Key ID
              </label>
              <input
                type="text"
                value={credentials.appleStore.keyId}
                onChange={(e) => updateAppleStore({ keyId: e.target.value })}
                className="input"
                placeholder="AB12CD34EF"
              />
              <p className="mt-1 text-xs text-gray-500">
                App Store Connect API Key ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issuer ID
              </label>
              <input
                type="text"
                value={credentials.appleStore.issuerId}
                onChange={(e) => updateAppleStore({ issuerId: e.target.value })}
                className="input"
                placeholder="12345678-1234-1234-1234-123456789012"
              />
              <p className="mt-1 text-xs text-gray-500">
                App Store Connect Issuer ID
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bundle ID Pattern
            </label>
            <input
              type="text"
              value={credentials.appleStore.bundleId}
              onChange={(e) => updateAppleStore({ bundleId: e.target.value })}
              className="input"
              placeholder="com.servi.*"
            />
            <p className="mt-1 text-xs text-gray-500">
              Base bundle identifier pattern for your apps
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Private Key (.p8)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input
                type="file"
                accept=".p8"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileUpload('appleStore', file)
                }}
                className="hidden"
                id="apple-key-upload"
              />
              <label htmlFor="apple-key-upload" className="cursor-pointer">
                <div className="text-center">
                  <Key className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Click to upload App Store Connect private key
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Download from App Store Connect
                  </p>
                </div>
              </label>
              {credentials.appleStore.privateKey && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center space-x-2">
                    <Key className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800">Private key uploaded</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => testConnection('appleStore')}
                disabled={loading || !credentials.appleStore.teamId || !credentials.appleStore.keyId || !credentials.appleStore.privateKey}
                className="btn-secondary"
              >
                {loading ? 'Testing...' : 'Test Connection'}
              </button>
              <a
                href="https://appstoreconnect.apple.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-brand-600 hover:text-brand-700 flex items-center space-x-1"
              >
                <span>App Store Connect</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {credentials.appleStore.lastSync && (
              <span className="text-xs text-gray-500">
                Last synced: {new Date(credentials.appleStore.lastSync).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="card border-amber-200 bg-amber-50">
        <div className="p-6">
          <div className="flex items-start space-x-3">
            <Shield className="w-6 h-6 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900">Security Notice</h3>
              <p className="text-sm text-amber-800 mt-1">
                Your API keys and credentials are encrypted and stored securely. They are only used for automated app publishing and are never shared with third parties.
              </p>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• Keys are encrypted using AES-256 encryption</li>
                <li>• Access is logged and monitored</li>
                <li>• You can revoke access at any time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 